import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter, Redirect } from 'react-router';
import lodashCompact from 'lodash/fp/compact';

import parseJSON from 'common/util/parseJSON';

import {
  SCREEN_ID_FEED_CATEGORY_MAP,
} from 'client/constants/screenIds';
import {
  hasRoleAnonymous,
} from 'common/userRoles';
import {
  findFeedCategoryId,
  actionInitializeFeedCategoryState,
  actionGetFeedCategory,
} from 'client/helpers/feedCategoryHelpers';

import {
  FEED_CATEGORY_AREA_TO_TOUR,
} from 'common/feedCategoryConstants';
import withScreenId from 'client/helpers/withScreenId';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withLocationQuery from 'client/helpers/withLocationQuery';
import MapViewManagerActions from 'client/actions/MapViewManager';
import MarketActions from 'client/actions/Market';
import branch from 'client/core/branch';

import SrNavbar from 'client/components/SrNavbar';
import TabBar from 'client/components/TabBar';
import SrFeedCategoryMapView from 'client/components/SrFeedCategoryMapView';
import SrLink from 'client/components/SrLink/SrLink';
import NavbarButton from 'client/components/NavbarButton';

@withRouter
@withTransitionHook
@withScreenId(SCREEN_ID_FEED_CATEGORY_MAP)
@withLocationQuery
@branch({
  currentUser: ['currentUser'],
  mapViewById: ['mapViewById'],
  bookmarksByListingId: ['bookmarksByListingId'],
  listingPhotoResolution: ['listingPhotoResolution'],
  viewedListings: ['listingsMapView', 'viewedListings'],
  currentUserMarket: ['markets', 'currentUserMarket'],
  neighborhoodsByMarketId: ['markets', 'neighborhoodsByMarketId'],
})
class FeedCategoryMapPage extends Component {
  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    mapViewById: PropTypes.object.isRequired,
    bookmarksByListingId: PropTypes.object,
    viewedListings: PropTypes.array.isRequired,
    listingPhotoResolution: PropTypes.number.isRequired,
    match: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    locationQuery: PropTypes.object,
    currentUserMarket: PropTypes.object.isRequired,
    neighborhoodsByMarketId: PropTypes.object.isRequired,
  }

  static fetchData(state, inject) {
    const currentUser = inject.state.get(['currentUser']);

    return Promise.all([
      MarketActions.getNeighborhoodsByMarketId(inject, currentUser.searchFilters.marketId),
    ]);
  }

  UNSAFE_componentWillMount() {
    this._createMapView();
  }

  _getMapViewId = () => {
    const { locationQuery, match } = this.props;
    const feedCategoryId = findFeedCategoryId(match.params.feedCategoryId);
    const mapViewPath = [
      feedCategoryId,
      match.params.mapArea,
    ];

    if (
      match.params.mapArea === 'search'
      && locationQuery
      && locationQuery.mapCenterLat
      && locationQuery.mapCenterLng
    ) {
      mapViewPath.push(locationQuery.mapCenterLat);
      mapViewPath.push(locationQuery.mapCenterLng);
    }

    return lodashCompact(mapViewPath).join('-');
  }

  _createMapView = () => {
    const {
      match,
      dispatch,
      currentUserMarket,
      neighborhoodsByMarketId,
      locationQuery,
    } = this.props;
    const feedCategoryId = findFeedCategoryId(match.params.feedCategoryId);
    dispatch(actionInitializeFeedCategoryState, { feedCategoryId });
    const { feedCategoryState } = dispatch(actionGetFeedCategory, { feedCategoryId });

    const initialMapViewState = {
      mapViewId: this._getMapViewId(),
      data: {
        listings: feedCategoryState ? feedCategoryState.listings : [],
        viewingListingId: null,
        centerMarkers: true,

        mapArea: null,
        mapZoomLevel: locationQuery && locationQuery.mapZoomLevel
          ? parseInt(locationQuery.mapZoomLevel, 10)
          : 11,
        mapCenter: locationQuery && (locationQuery.mapCenterLat && locationQuery.mapCenterLng)
          ? {
            lat: parseFloat(locationQuery.mapCenterLat),
            lng: parseFloat(locationQuery.mapCenterLng),
          }
          : null,
      },
    };

    if (feedCategoryId === FEED_CATEGORY_AREA_TO_TOUR && match.params.mapArea) {
      initialMapViewState.data.centerMarkers = false;
      initialMapViewState.data.listings = [];

      const neighborhoods = neighborhoodsByMarketId[currentUserMarket.id];
      const neighborhood = neighborhoods
        .find((neighborhood) => `${neighborhood.id}` === match.params.mapArea);

      if (neighborhood) {
        const mapArea = parseJSON(neighborhood.mapArea);
        initialMapViewState.data.mapArea = mapArea.coordinates;
        initialMapViewState.data.mapZoomLevel = neighborhood.mapZoomLevel;
      }
    }

    dispatch(MapViewManagerActions.newMapView, initialMapViewState);
  }

  render() {
    const {
      match,
      history,
      mapViewById,
      currentUser,
      bookmarksByListingId,
      listingPhotoResolution,
      viewedListings,
    } = this.props;

    const feedCategoryId = findFeedCategoryId(match.params.feedCategoryId);
    const isAnonymous = hasRoleAnonymous(currentUser.roles);

    if (!feedCategoryId) {
      return <Redirect to='/feed' />;
    }

    if (feedCategoryId === FEED_CATEGORY_AREA_TO_TOUR && !match.params.mapArea) {
      return <Redirect to='/address-search' />;
    }

    const mapViewId = this._getMapViewId();

    const leftNavbarButtonDOM = (
      <SrLink
        className='back link'
        restoreScrollPosition
        to={feedCategoryId === FEED_CATEGORY_AREA_TO_TOUR ? '/address-search' : '/feed'}
      >
        Back
      </SrLink>
    );

    return (
      <div className='SrPage SrListviewPage'>
        <SrNavbar
          leftButton={leftNavbarButtonDOM}
          rightButton={feedCategoryId === FEED_CATEGORY_AREA_TO_TOUR ? null : (
            <NavbarButton
              onClick={() => {
                history.push(`/feed/${feedCategoryId}`);
              }}
            >
              List
            </NavbarButton>
          )}
        />

        {!isAnonymous && (
          <TabBar />
        )}

        {mapViewById[mapViewId] && (
          <SrFeedCategoryMapView
            mapViewId={mapViewId}
            feedCategoryId={feedCategoryId}
            listingPhotoResolution={listingPhotoResolution}
            mapState={{
              ...mapViewById[mapViewId],
              currentUser,
              viewedListings,
              bookmarksByListingId: Object.keys(bookmarksByListingId || {}),
            }}
          />
        )}
      </div>
    );
  }
}

FeedCategoryMapPage.displayName = 'FeedCategoryMapPage';

export default FeedCategoryMapPage;
